import { useEffect, useRef, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0AvatarPerson, F0Checkbox, F0Link, F0Select } from '@factorialco/f0-react'
import {
  RichTextEditor,
  RichTextEditorHandle,
  Switch,
  resultType,
} from '@factorialco/f0-react/dist/experimental'
import { Calendar, FileFilled } from '@factorialco/f0-react/icons/app'
import { PostsGroup } from 'generated/resources/datatable.graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import TextField from 'modules/core/components/TextField'
import TimePicker from 'modules/core/components/TimePicker'
import { Access, Employee } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import buildUrl from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import SchedulePostModal from 'modules/posts/components/SchedulePost/SchedulePostModal'
import { useCreatePost } from 'modules/posts/hooks/useCreatePost'

import * as styles from './index.vanilla.css'

interface CommunityQuickPostEditorProps {
  groupId: number
  inCommunityPage: boolean
  communities: Array<{
    id: PostsGroup['id']
    title: PostsGroup['title']
  }>
  currentUser: {
    employeeId: Employee['id']
    firstName: Access['firstName']
    lastName: Access['lastName']
    fullName?: Access['fullName']
    avatar?: Access['avatar']
  }
}

type PostForm = {
  allowCommentsAndReactions: boolean
  attachments?: Array<File>
  coverImage?: Array<File>
  description: string
  location?: string
  mentionedAccessIds: Array<number>
  postGroupId: number
  publishedAt: string | null
  sendNotifications: boolean
  startsAt?: string
  title: string
  type: 'announcement' | 'event' | 'kudo'
}

type SubmitPostMode = 'publish' | 'schedule' | 'draft'

function getSuccessMessage(mode: SubmitPostMode) {
  switch (mode) {
    case 'publish':
      return i18n.t('posts.communities.quick_post.success_published')
    case 'schedule':
      return i18n.t('posts.communities.quick_post.success_scheduled')
    case 'draft':
      return i18n.t('posts.communities.quick_post.success_saved_as_draft')
    default:
      return i18n.t('posts.communities.quick_post.success_published')
  }
}

function getPublishedAtForMode(mode: SubmitPostMode, scheduledDate?: string): string | null {
  switch (mode) {
    case 'publish':
      return moment().toISOString()
    case 'schedule':
      return scheduledDate ?? moment().toISOString()
    case 'draft':
      return null
    default:
      return moment().toISOString()
  }
}

function CommunityQuickPostEditor({
  groupId,
  inCommunityPage,
  communities = [],
  currentUser,
}: CommunityQuickPostEditorProps) {
  const { editorConfig, loading: loadingEditorConfig } = useRichTextEditor()
  const editorRef = useRef<RichTextEditorHandle>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [focused, setFocused] = useState(false)
  const [showScheduleModal, setShowScheduleModal] = useState(false)

  const { createPost, loadingCreatePost } = useCreatePost()

  async function submitPost(mode: SubmitPostMode, scheduledDate?: string) {
    const publishedAt = getPublishedAtForMode(mode, scheduledDate)
    const nextValues = { ...postForm.values, publishedAt }
    await postForm.setValues(nextValues)
    const result = await createPost(nextValues)
    const handledResult = handleResult(result, () => {})

    if (handledResult.success) {
      resetEditor()
      const successMessage = getSuccessMessage(mode)
      app.setFlash({ type: 'correct', message: successMessage })
    } else {
      const errorMessage = i18n.t('posts.communities.quick_post.error_message')
      app.setFlash({ type: 'wrong', message: errorMessage })
    }

    return handledResult
  }

  const postForm = useForm<PostForm>({
    defaultValues: {
      allowCommentsAndReactions: true,
      attachments: undefined,
      coverImage: undefined,
      description: '',
      location: undefined,
      mentionedAccessIds: [],
      postGroupId: groupId,
      publishedAt: moment().toISOString(),
      sendNotifications: true,
      startsAt: undefined,
      title: '',
      type: 'announcement',
    },
    onSubmit: async () => submitPost('publish'),
  })

  function handleSchedule(scheduledDate: string) {
    submitPost('schedule', scheduledDate)
    setShowScheduleModal(false)
  }

  function handleSaveDraft() {
    submitPost('draft')
  }

  useEffect(() => {
    if (focused) {
      editorRef.current?.focus()
    }
  }, [focused])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (showScheduleModal) return
      if (containerRef.current === null) return

      const rect = containerRef.current.getBoundingClientRect()
      const clickX = event.clientX
      const clickY = event.clientY

      const clickInsideQuickPostEditor =
        clickX >= rect.left && clickX <= rect.right && clickY >= rect.top && clickY <= rect.bottom

      if (!clickInsideQuickPostEditor) {
        setFocused(false)
      }
    }

    if (focused) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [focused, showScheduleModal])

  function resetEditor() {
    postForm.setValues({
      ...postForm.values,
      description: '',
      location: '',
      postGroupId: groupId,
      startsAt: undefined,
      title: '',
      type: 'announcement',
      allowCommentsAndReactions: true,
      sendNotifications: true,
      attachments: undefined,
      coverImage: undefined,
      mentionedAccessIds: [],
    })
    setFocused(false)
  }

  useEffect(resetEditor, [groupId, inCommunityPage])

  function getCommunityNameById(id: number): string {
    const community = communities.find((c) => c.id === id)
    return community ? community.title : ''
  }

  function onChangeCommunity(nextCommunityId: string) {
    const selectedCommunity = communities.find((c) => c.id.toString() === nextCommunityId)
    if (selectedCommunity) {
      postForm.setValues({ ...postForm.values, postGroupId: selectedCommunity.id })
    }
  }

  function onChangePostContent(result: resultType) {
    const description = result.value ?? ''
    const title = getTitleFromContent(description)
    postForm.setValues({ ...postForm.values, title, description })
  }

  function getTitleFromContent(content: string): string {
    if (!content.trim()) return i18n.t('posts.communities.quick_post.default_title')

    let contentToUse = content

    const firstBreakIndex =
      ['<br>', '</p>']
        .map((pattern) => content.indexOf(pattern))
        .filter((index) => index !== -1)
        .sort((a, b) => a - b)[0] ?? -1

    if (firstBreakIndex !== -1) {
      contentToUse = content.substring(0, firstBreakIndex)
    }

    const plainText = removeHTMLTags(contentToUse)
    const maxLength = 60
    if (plainText.length <= maxLength) return plainText

    const truncated = plainText.substring(0, maxLength)
    const lastSpaceIndex = truncated.lastIndexOf(' ')
    const contentFirstWords =
      lastSpaceIndex > 0 ? truncated.substring(0, lastSpaceIndex) : truncated
    return `${contentFirstWords}...`
  }

  function removeHTMLTags(htmlString: string): string {
    return htmlString.replace(/<[^>]*>/g, '').trim()
  }

  function onChangeIsEvent(nextIsEvent: boolean) {
    const type = nextIsEvent ? 'event' : 'announcement'
    const currentEventTime = moment(postForm.values.startsAt).format('HH:mm')
    const currentEventDay = moment(postForm.values.startsAt).format('L')
    const currentEventDatetime = buildDateTimeFromDayAndTime(currentEventDay, currentEventTime)
    const startsAt = nextIsEvent ? currentEventDatetime : undefined
    postForm.setValues({ ...postForm.values, type, startsAt })
  }

  function onChangeDay(day: string) {
    const currentEventTime = moment(postForm.values.startsAt).format('HH:mm')
    const startsAt = buildDateTimeFromDayAndTime(day, currentEventTime)
    postForm.setValues({ ...postForm.values, startsAt })
  }

  function onChangeTime(time: string) {
    const currentEventDay = moment(postForm.values.startsAt).format('L')
    const startsAt = buildDateTimeFromDayAndTime(currentEventDay, time)
    postForm.setValues({ ...postForm.values, startsAt })
  }

  function buildDateTimeFromDayAndTime(day: string, time: string): string {
    const localeData = moment.localeData()
    const format = localeData.longDateFormat('L')
    const date = moment(day, format)
    const timeMoment = moment(time, 'HH:mm')
    const combinedDate = date
      .clone()
      .set('hour', timeMoment.hour())
      .set('minute', timeMoment.minute())
      .set('second', 0)
      .set('millisecond', 0)
    return combinedDate.format()
  }

  function onChangeAttachments(attachments: Array<File>) {
    postForm.setValues({ ...postForm.values, attachments })
  }

  const isEvent = postForm.values.type === 'event'
  const isPostDisabled = loadingCreatePost || !postForm.values.description.trim()

  if (loadingEditorConfig) {
    return <RichTextEditor.Skeleton rows={1} />
  }

  if (!focused) {
    return (
      <QuickPostEditorCollapsed
        communityName={getCommunityNameById(postForm.values.postGroupId)}
        currentUser={currentUser}
        handleFocus={() => setFocused(true)}
      />
    )
  }

  return (
    <Box ref={containerRef} gap="s16" className={styles.editorContainer}>
      {currentUser && (
        <Box
          flexDirection="row"
          alignItems="center"
          gap="s12"
          role={'region'}
          aria-label={i18n.t('posts.communities.quick_post.header_label')}
        >
          <F0Link
            href={`/employees/${currentUser.employeeId}`}
            title={currentUser.firstName}
            className="font-medium text-f1-foreground no-underline visited:text-f1-foreground"
            stopPropagation
          >
            <F0AvatarPerson
              firstName={currentUser.firstName}
              lastName={currentUser.lastName}
              src={buildUrl(currentUser.avatar)}
            />
          </F0Link>
          <Box flexDirection="row" alignItems="center" gap="s8" style={{ width: '100%' }}>
            <F0Link
              href={`/employees/${currentUser.employeeId}`}
              title={currentUser.fullName}
              className="font-medium text-f1-foreground no-underline visited:text-f1-foreground"
              style={{ flexShrink: 0 }}
              stopPropagation
            >
              {`${currentUser.fullName}`}
            </F0Link>
            <Text.Normal size="200" color="grey800">
              {i18n.t('common.in')}
            </Text.Normal>
            <Box style={{ flexShrink: '0' }}>
              <F0Select
                size="md"
                label={i18n.t('settings.locations.location.work_spaces.add.type')}
                value={postForm.values.postGroupId.toString()}
                options={communities.map((community) => ({
                  label: community.title,
                  value: community.id.toString(),
                }))}
                onChange={onChangeCommunity}
                disabled={inCommunityPage}
                hideLabel
              />
            </Box>
          </Box>
        </Box>
      )}
      <RichTextEditor
        ref={editorRef}
        {...editorConfig}
        title={getTitleFromContent(postForm.values.description)}
        placeholder={i18n.t('posts.communities.quick_post.placeholder')}
        maxCharacters={10000}
        initialEditorState={{ content: postForm.values.description }}
        onChange={onChangePostContent}
        filesConfig={{
          acceptedFileType: ['image', 'video', 'pdf'],
          multipleFiles: true,
          onFiles: onChangeAttachments,
        }}
        primaryAction={{
          action: {
            label: loadingCreatePost
              ? i18n.t('common.saving')
              : i18n.t('posts.communities.quick_post.button'),
            onClick: () => submitPost('publish'),
            variant: 'default',
            disabled: isPostDisabled,
          },
          ...(!isPostDisabled && {
            subActions: [
              {
                label: i18n.t('posts.communities.schedule_post.title'),
                onClick: () => setShowScheduleModal(true),
                icon: Calendar,
              },
              {
                label: i18n.t('posts.communities.quick_post.save_as_draft'),
                onClick: handleSaveDraft,
                icon: FileFilled,
              },
            ],
          }),
        }}
      />
      <Box gap={'s12'} paddingX="s16" paddingY="s16" className={styles.isEventContainer}>
        <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
          <Box gap="s4">
            <Text.Normal size="200" weight="medium" color="black">
              {i18n.t('posts.communities.quick_post.is_event_label')}
            </Text.Normal>
            <Text.Normal size="100">
              {i18n.t('posts.communities.quick_post.is_event_description')}
            </Text.Normal>
          </Box>
          <Switch
            checked={isEvent}
            onCheckedChange={onChangeIsEvent}
            title={i18n.t('posts.communities.quick_post.is_event_label')}
            hideLabel
          />
        </Box>
        {isEvent && (
          <Box flexDirection="row" gap="s12">
            <Box gap={'s6'} style={{ width: '30%' }}>
              <DayPicker
                value={(() => {
                  return postForm.values.startsAt !== undefined
                    ? moment(postForm.values.startsAt).format('L')
                    : moment().format('L')
                })()}
                onChange={onChangeDay}
                minDate={moment().startOf('day')}
                mandatory={isEvent}
                label={i18n.t('posts.communities.quick_post.day_picker_label')}
              />
            </Box>
            <Box gap={'s6'} style={{ width: '20%' }}>
              <TimePicker
                value={(() => {
                  return postForm.values.startsAt
                    ? moment(postForm.values.startsAt).format('HH:mm')
                    : moment().format('HH:mm')
                })()}
                onChange={onChangeTime}
                attachmentPosition="bottom right"
                mandatory={isEvent}
                label={i18n.t('posts.communities.quick_post.time_picker_label')}
              />
            </Box>
            <Box gap={'s6'} style={{ width: '50%' }}>
              <TextField
                value={postForm.values.location || ''}
                placeholder={i18n.t('posts.communities.quick_post.event_location_placeholder')}
                onChange={(location) => postForm.setValues({ ...postForm.values, location })}
                label={i18n.t('posts.communities.quick_post.location_label')}
              />
            </Box>
          </Box>
        )}
      </Box>
      <F0Checkbox
        title={i18n.t('posts.communities.quick_post.turn_off_interactions')}
        checked={!postForm.values.allowCommentsAndReactions}
        onCheckedChange={(checked: boolean) => {
          postForm.setValues({ ...postForm.values, allowCommentsAndReactions: !checked })
        }}
      />
      <F0Checkbox
        title={i18n.t('posts.communities.quick_post.do_not_send_notification')}
        checked={!postForm.values.sendNotifications}
        onCheckedChange={(checked: boolean) => {
          postForm.setValues({ ...postForm.values, sendNotifications: !checked })
        }}
      />
      <SchedulePostModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onConfirm={handleSchedule}
      />
    </Box>
  )
}

function QuickPostEditorCollapsed({
  communityName,
  currentUser,
  handleFocus,
}: {
  communityName: string
  currentUser: CommunityQuickPostEditorProps['currentUser']
  handleFocus: () => void
}) {
  return (
    <Box
      gap="s12"
      flexDirection="row"
      alignItems="center"
      className={styles.collapsedEditorContainer}
    >
      <F0AvatarPerson
        firstName={currentUser.firstName}
        lastName={currentUser.lastName}
        src={buildUrl(currentUser.avatar)}
      />
      <TextField
        onFocus={handleFocus}
        placeholder={i18n.t('posts.communities.quick_post.collapsed_placeholder', {
          communityName,
        })}
        label={i18n.t('posts.communities.quick_post.collapsed_label')}
        hideLabel
      />
    </Box>
  )
}

export default CommunityQuickPostEditor
